import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip,
    Skeleton,
    Alert,
    Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FeatureService from "../../services/FeatureService";

// ─── Helpers ────────────────────────────────────────────────────────────────

const stateChipProps = (state) => {
    const isEnabled = state === "Enabled";
    return {
        label: state,
        size: "small",
        icon: isEnabled ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> : <HighlightOffIcon sx={{ fontSize: 16 }} />,
        sx: {
            fontWeight: 600,
            color: isEnabled ? "#166534" : "#64748b",
            backgroundColor: isEnabled ? "#dcfce7" : "#f1f5f9",
            "& .MuiChip-icon": {
                color: isEnabled ? "#16a34a" : "#94a3b8",
            },
        },
    };
};

const usageColor = (usage) => {
    if (usage >= 90) return "#dc2626"; // red — nearly exhausted
    if (usage >= 70) return "#d97706"; // amber — getting close
    return "#1e3a5f"; // navy — healthy, matches app's primary accent
};

// ─── Component ──────────────────────────────────────────────────────────────

const MSStatus = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadFeatures = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await FeatureService.getAvailableFeatures();
            setFeatures(data?.features || []);
        } catch (err) {
            setError(typeof err === "string" ? err : "Failed to load feature status.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFeatures();
    }, [loadFeatures]);

    const embedTrial = useMemo(
        () => features.find((f) => f.name === "embedTrial"),
        [features]
    );

    const usage = embedTrial?.additionalInfo?.usage ?? 0;

    return (
        <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                        Embed Trial Token Usage
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                        Power BI trial capacity status for this tenant
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={loadFeatures} disabled={loading} sx={{ color: "#1e3a5f" }}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading && !error && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Skeleton width={220} height={28} />
                        <Skeleton width="100%" height={10} sx={{ mt: 2 }} />
                        <Skeleton width={140} height={20} sx={{ mt: 1 }} />
                    </CardContent>
                </Card>
            )}

            {!loading && !error && embedTrial && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: usage >= 90 ? "#fca5a5" : usage >= 70 ? "#fcd34d" : "#e2e8f0",
                    }}
                >
                    <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                    Trial Tokens
                                </Typography>
                                {usage >= 70 && (
                                    <Tooltip title="Trial token pool is running low — capacity purchase recommended">
                                        <WarningAmberIcon sx={{ fontSize: 20, color: "#d97706" }} />
                                    </Tooltip>
                                )}
                            </Stack>
                            <Chip {...stateChipProps(embedTrial.state)} />
                        </Stack>

                        <Typography variant="h4" sx={{ fontWeight: 700, color: usageColor(usage) }}>
                            {usage}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                            of trial embed token allowance used
                        </Typography>

                        <LinearProgress
                            variant="determinate"
                            value={usage}
                            sx={{
                                mt: 1.5,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: "#f1f5f9",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 4,
                                    backgroundColor: usageColor(usage),
                                },
                            }}
                        />

                        {usage >= 70 && (
                            <Typography variant="caption" sx={{ color: "#92400e", mt: 1, display: "block" }}>
                                Trial tokens are for development/testing only and are limited in supply.
                                Once exhausted, new report embeds will fail. Consider moving this workspace
                                to a Premium, Embedded, or Fabric capacity.
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {!loading && !error && !embedTrial && (
                <Alert severity="info">No trial token data available for this tenant.</Alert>
            )}
        </Box>
    );
};

export default MSStatus;